import React, {useEffect, useState} from 'react'
import './PreviewWalletCard.scss'
import {PreviewWalletCardGoogle} from './PreviewWalletCardGoogle/PreviewWalletCardGoogle'
import {PreviewWalletCardApple} from './PreviewWalletCardApple/PreviewWalletCardApple'
import {PreviewWalletCardContact} from './PreviewWalletCardContact/PreviewWalletCardContact'
import {CardViewType, STEP_KEYS} from '../../../../../Utils/Constant/WalletCard'
import {ExtendedPreviewWalletCardProps} from '../../type'

export const PreviewWalletCard: React.FC<ExtendedPreviewWalletCardProps> = ({
  cardData,
  onViewChange,
  promoSettingIds,
  formFields,
  promoActionLabels,
  expandedStep,
  setExpandedStep,
  disableStepSync = false,
}) => {
  // State to keep track of which card view to display (Google, Apple, Contact)
  const [currentView, setCurrentView] = useState<CardViewType>(CardViewType.GOOGLE)
  // Handle switching between different card preview views
  const handleViewChange = (viewType: CardViewType) => {
    setCurrentView(viewType)
    if (onViewChange) onViewChange(viewType)

    if (!disableStepSync) {
      // Sync with stepper - when switching to Form Setting, expand the Form Connect step
      if (viewType === CardViewType.FORM_SETTING && setExpandedStep) {
        setExpandedStep(STEP_KEYS.FORM_CONNECT)
      }
      // When switching away from Form Setting while in Form Connect step,
      // automatically switch to Share Contact Details step to maintain consistency
      else if (
        viewType !== CardViewType.FORM_SETTING &&
        expandedStep === STEP_KEYS.FORM_CONNECT &&
        setExpandedStep
      ) {
        setExpandedStep(STEP_KEYS.CARD_DETAILS)
      }
    }
  }

  // Effect to sync view with expanded step
  useEffect(() => {
    if (disableStepSync) return

    if (expandedStep === STEP_KEYS.FORM_CONNECT) {
      // Always switch to FORM_SETTING when Form Connect step is active
      if (currentView !== CardViewType.FORM_SETTING) {
        setCurrentView(CardViewType.FORM_SETTING)
      }
    } else {
      // If we're in FORM_SETTING view but step is not FORM_CONNECT, switch to CONTACT view
      if (currentView === CardViewType.FORM_SETTING) {
        setCurrentView(CardViewType.CONTACT)
      }
    }
  }, [expandedStep, currentView, disableStepSync])

  // Effect to sync onViewChange callback
  useEffect(() => {
    if (onViewChange) {
      onViewChange(currentView)
    }
  }, [currentView, onViewChange])

  // Render the appropriate card preview based on current view
  const renderCardView = () => {
    const customStyles = cardData.backgroundColor ? {backgroundColor: cardData.backgroundColor} : {}
    switch (currentView) {
      case CardViewType.GOOGLE:
        return <PreviewWalletCardGoogle cardData={cardData} customStyles={customStyles} />
      case CardViewType.APPLE:
        return <PreviewWalletCardApple cardData={cardData} customStyles={customStyles} />
      case CardViewType.CONTACT:
        return <PreviewWalletCardContact cardData={cardData} />
      case CardViewType.FORM_SETTING:
        return (
          <PreviewWalletCardContact
            cardData={cardData}
            showFormSettingModal={true}
            settingFormFields={formFields}
            promoActionLabels={promoActionLabels}
          />
        )
      default:
        return <PreviewWalletCardGoogle cardData={cardData} customStyles={customStyles} />
    }
  }

  // Main render
  return (
    <div className='preview-card-container'>
      <div className='icon-buttons-wrapper-top'>
        <button
          className={`icon-wrapper ${currentView === CardViewType.GOOGLE ? 'active' : ''}`}
          onClick={() => handleViewChange(CardViewType.GOOGLE)}
        >
          <i className='bi bi-google icon-google'></i>
        </button>
        <button
          className={`icon-wrapper ${currentView === CardViewType.APPLE ? 'active' : ''}`}
          onClick={() => handleViewChange(CardViewType.APPLE)}
        >
          <i className='bi bi-apple icon-apple'></i>
        </button>
        <button
          className={`icon-wrapper ${currentView === CardViewType.CONTACT ? 'active' : ''}`}
          onClick={() => handleViewChange(CardViewType.CONTACT)}
        >
          <img src='/media/icons/phone_book.svg' alt='Phone Book' className='icon-phone-book' />
        </button>
        <button
          className={`icon-wrapper ${currentView === CardViewType.FORM_SETTING ? 'active' : ''}`}
          onClick={() => handleViewChange(CardViewType.FORM_SETTING)}
        >
          <img
            src='/media/icons/form_setting.svg'
            alt='Form Setting'
            className='icon-form-setting'
          />
        </button>
      </div>

      {/* Card Preview Section */}
      <div className='card-preview'>
        {/* Platform Icons - Right side for desktop */}
        <div className='icon-buttons-wrapper-desktop'>
          <button
            className={`icon-wrapper-top ${currentView === CardViewType.GOOGLE ? 'active' : ''}`}
            onClick={() => handleViewChange(CardViewType.GOOGLE)}
          >
            <i className='bi bi-google icon-google'></i>
          </button>
          <button
            className={`icon-wrapper-middle ${currentView === CardViewType.APPLE ? 'active' : ''}`}
            onClick={() => handleViewChange(CardViewType.APPLE)}
          >
            <i className='bi bi-apple icon-apple'></i>
          </button>
          <button
            className={`icon-wrapper-middle ${
              currentView === CardViewType.CONTACT ? 'active' : ''
            }`}
            onClick={() => handleViewChange(CardViewType.CONTACT)}
          >
            <img src='/media/icons/phone_book.svg' alt='Phone Book' className='icon-phone-book' />
          </button>
          <button
            className={`icon-wrapper-bottom ${
              currentView === CardViewType.FORM_SETTING ? 'active' : ''
            }`}
            onClick={() => handleViewChange(CardViewType.FORM_SETTING)}
          >
            <img
              src='/media/icons/form_setting.svg'
              alt='Form Setting'
              className='icon-form-setting'
            />
          </button>
        </div>
        <div className='outer-frame me-4'>
          <div className='inner-screen'>{renderCardView()}</div>
        </div>
      </div>
    </div>
  )
}

export default PreviewWalletCard
